// Ponte entre a Jornada e o tour guiado, do lado do Fee Fixo.
//
// É aqui que os dois lados se encontram: o TEXTO dos passos vem do catálogo
// (etapa crua do backend), os SELETORES vêm de `constants/tour-fee-fixo.js`, e
// o resultado casado vai para o slice do central-config. A casca que renderiza
// o tour nunca precisa saber que o Fee Fixo existe.
//
// ★ Desde 2026-08-14 o caminho principal é o REGISTRY: `montarTourDoFeeFixo` é
// registrado em `index.js` do módulo e consultado por `startTaskNavigation`.
// Com isso TODA superfície de "Iniciar etapa" guia — a barra embutida na Visão
// Geral, o accordion da Jornada, o card da Home e o CTA da landing — sem que
// nenhuma delas precise chamar algo especial.
import { startTaskNavigation } from '@modules/central-config/utils/step-navigation'
import { GUIDE_ORIGIN_MODULE } from '@modules/central-config/utils/guide-origin'
import {
  montarPassosDoTour,
  proximaEtapaGuiavel,
} from '@modules/central-config/utils/tour-steps'
import {
  moduloDoCatalogo,
  nivelDoModulo,
  moduloAteNivel,
  essentialTasksNaOrdemVisual,
} from '@modules/central-config/utils/progress'
import {
  PASSOS_DO_TOUR,
  MODULE_KEY_FEE_FIXO,
  temTourGuiado,
} from '@modules/fee-fixo/constants/tour-fee-fixo'

// Reexport, NUNCA uma segunda string: o canal é declarado no central-config
// (quem emite) e apenas consumido aqui (quem escuta).
export { CENTRAL_CONFIG_TOUR_PROXIMA_ETAPA as EVENTO_PROXIMA_ETAPA } from '@modules/central-config/constants/Text'

const STATUS_DONE = 'done'

const moduloDoStore = (store) => {
  const getters = (store && store.getters) || {}
  const modules = getters['centralConfig/journeyModules'] || []
  return moduloDoCatalogo(modules, MODULE_KEY_FEE_FIXO)
}

// A etapa crua do catálogo (com metadata.instructions[]) não é a mesma coisa
// que a etapa normalizada que o card exibe — o tour precisa das DUAS: a crua
// pelo texto, a normalizada pelo status/indisponibilidade.
const acharEtapaCrua = (modulo, taskId) => {
  const tasks = (modulo && modulo.tasks) || []
  return tasks.find((task) => task && task.id === taskId) || null
}

// Etapa cujo `premium_feature` o escritório não contratou é VITRINE: aparece na
// jornada de propósito, mas não pode ser oferecida como "próxima" — a tela não
// executa. O mixin de visibilidade carimba isso em componente; aqui, que é
// util puro chamado de dentro da navegação, checamos a flag direto.
const semAcessoPremium = (store, task) => {
  const metadata = (task && task.metadata) || {}
  const premium = metadata.premium_feature
  if (!premium) return false
  const show = (store && store.getters['parameters/show']) || {}
  // `show` vazio (ainda carregando) não pode esconder trilha do escritório.
  if (!Object.keys(show).length) return false
  return !show[premium]
}

// Etapas do módulo na ordem visual, já cortadas no nível atual — a mesma fatia
// que o card da Home e a barra embutida exibem.
const etapasDoModulo = (store, modulo) => {
  const ateNivel = moduloAteNivel(modulo, nivelDoModulo(modulo))
  return essentialTasksNaOrdemVisual(ateNivel).map((task) => {
    const metadata = (task && task.metadata) || {}
    return {
      id: task.id,
      slug: task.slug || '',
      name: task.name || '',
      url: task.url || '',
      isDone: metadata.status === STATUS_DONE,
      isIndisponivel: !!metadata.indisponivel || semAcessoPremium(store, task),
    }
  })
}

/**
 * Provedor de tour do Fee Fixo — registrado em `index.js` e chamado por
 * `startTaskNavigation`. Devolve o payload de `iniciarTour`, ou null quando a
 * etapa não tem tour mapeado (aí a navegação segue com o drawer de texto).
 */
export function montarTourDoFeeFixo({ store, moduleId, task } = {}) {
  if (!store || !task || !temTourGuiado(task.slug)) return null

  const modulo = moduloDoStore(store)
  const crua = acharEtapaCrua(modulo, task.id)
  const passos = montarPassosDoTour({
    task: crua,
    mapa: PASSOS_DO_TOUR[task.slug],
  })
  if (!passos.length) return null

  const proxima = proximaEtapaGuiavel({
    etapas: etapasDoModulo(store, modulo),
    temTour: temTourGuiado,
    slugAtual: task.slug,
  })

  return {
    moduleId: moduleId || (modulo && modulo.id) || null,
    moduleKey: MODULE_KEY_FEE_FIXO,
    taskId: task.id,
    taskSlug: task.slug,
    passos,
    proximaEtapa: proxima
      ? { taskId: proxima.id, slug: proxima.slug, rotulo: proxima.name }
      : null,
  }
}

/**
 * Inicia o tour de uma etapa navegando até ela. Hoje é só um atalho para
 * `startTaskNavigation` — quem monta o tour é o provedor acima, chamado por
 * dentro dela. Continua existindo porque o botão "Me guie" e o encadeamento
 * precisam de um verbo próprio, e porque os guards (etapa indisponível,
 * navegação em voo) são deles, não da navegação genérica.
 *
 * @returns {Boolean} false quando não há o que guiar
 */
export function iniciarTourDaEtapa({ store, router, modulo, etapa } = {}) {
  if (!store || !router || !modulo || !etapa) return false
  if (!temTourGuiado(etapa.slug)) return false

  // `skipChoreography`: a coreografia de saída (tingir a linha, deslizar a
  // lista) anima elementos do accordion da Jornada, que não existem nem no card
  // bespoke da Home nem no shell do módulo.
  startTaskNavigation({
    store,
    router,
    moduleId: modulo.id,
    task: etapa,
    origin: GUIDE_ORIGIN_MODULE,
    moduleKey: MODULE_KEY_FEE_FIXO,
    skipChoreography: true,
  })

  return true
}

/**
 * Primeira etapa pendente que tem tour mapeado. É o que o auto-start guia, e o
 * que o card usa para saber se há algo a oferecer.
 */
export function primeiraEtapaGuiavel(etapasVisiveis) {
  return proximaEtapaGuiavel({
    etapas: etapasVisiveis || [],
    temTour: temTourGuiado,
  })
}
